import { useCallback, useState } from 'react';

import { apiService } from '../../services';
import { VIDA_SEGURADO_MAINTENANCE_ROUTES } from '../../services/requestsService';
import { useUserStore } from '../../store/userStore';
import type { NewInsuredFormValues } from '../../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type { SelfBillingTableItem } from '../../types/vida/table/selfBillingTableType';
import type { SeguradoDetailBeneficiaryItem } from '../../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import {
  assertAddConjugeSeguradoDataResponse,
  assertAddSeguradoDataResponse,
  assertUpdateSeguradoDataResponse,
  buildAddConjugeSeguradoDataRequest,
  buildAddSeguradoDataRequest,
  buildConsultarSeguradosFaturamentoParams,
  buildRemoveSeguradoDataRequest,
  buildSeguradoListSearchParams,
  buildUpdateSeguradoDataRequest,
  findNumVidaPreviaFatFromConsulta,
  hasSeguradoBeneficiaries,
  isSelfBillingApiErrorMessage,
  mapSeguradoDetailToFormValues,
  mapSeguradoDetailToTableRow,
  mapSeguradoListItemToTableRow,
  mapSeguradoTableRowToFormValues,
  parseSeguradoInconsistenciesFromError,
  resolveCodigoUsuarioInclusao,
  SEGURADO_BILLING_STATUS,
  SeguradoInconsistencyError,
} from '../../utils/selfBillingUtil';

export type SelfBillingListQuery = {
  identSubestipulante: number;
  search?: string;
  page?: number;
  size?: number;
};

export type SelfBillingMutationParams = {
  identSubestipulante: number;
  contrato: number;
  formValues: NewInsuredFormValues;
  conjugueFormValues?: NewInsuredFormValues | null;
  beneficiarios?: SeguradoDetailBeneficiaryItem[];
  listQuery: SelfBillingListQuery;
};

export type SelfBillingRemoveParams = {
  identSubestipulante: number;
  ids: string[];
  listQuery: SelfBillingListQuery;
};

export type SelfBillingErrorSource = 'list' | 'detail' | 'add' | 'update' | 'remove' | 'addConjuge';

export const getSelfBillingErrorRoute = (source: SelfBillingErrorSource): string => {
  switch (source) {
    case 'list':
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.GET_LIST_SEGURADO_MAINTENANCE;
    case 'detail':
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.GET_SEGURADO_MAINTENANCE_DETAIL;
    case 'add':
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_ADD_SEGURADO_MAINTENANCE;
    case 'addConjuge':
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_ADD_CONJUGUE_SEGURADO_MAINTENANCE;
    case 'update':
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_ALTER_SEGURADO_MAINTENANCE;
    case 'remove':
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.POST_REMOVE_SEGURADO_MAINTENANCE;
    default:
      return VIDA_SEGURADO_MAINTENANCE_ROUTES.GET_LIST_SEGURADO_MAINTENANCE;
  }
};

export interface UseSelfBillingListReturn {
  isLoading: boolean;
  isDeleting: boolean;
  error: string | null;
  rows: SelfBillingTableItem[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  clearError: () => void;
  reset: () => void;
  loadSeguradoList: (
    identSubestipulante: number,
    options?: {
      search?: string;
      page?: number;
      size?: number;
    },
  ) => Promise<void>;
  removeSegurados: (
    identSubestipulante: number,
    ids: string[],
  ) => Promise<void>;
}

export interface UseSelfBillingDetailReturn {
  isSaving: boolean;
  isAdding: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  formValues: NewInsuredFormValues | null;
  selectedRow: SelfBillingTableItem | null;
  beneficiarios: SeguradoDetailBeneficiaryItem[];
  hasBeneficiarios: boolean;
  conjugueFormValues: NewInsuredFormValues | null;
  hasConjugue: boolean;
  setBeneficiarios: (beneficiarios: SeguradoDetailBeneficiaryItem[]) => void;
  setConjugueFormValues: (values: NewInsuredFormValues | null) => void;
  clearError: () => void;
  reset: () => void;
  loadSeguradoDetail: (row: SelfBillingTableItem) => Promise<void>;
  addSeguradoData: (
    idSubestipulante: number,
    contrato: number,
    formValues: NewInsuredFormValues,
  ) => Promise<void>;
  addConjugeSeguradoData: (
    idSubestipulante: number,
    contrato: number,
    formValues: NewInsuredFormValues,
  ) => Promise<void>;
  updateSeguradoData: (
    idSubestipulante: number,
    contrato: number,
    formValues: NewInsuredFormValues,
  ) => Promise<void>;
}

export interface UseSelfBillingReturn {
  list: UseSelfBillingListReturn;
  detail: UseSelfBillingDetailReturn;
  syncSeguradoList: (hasSearched: boolean, query: SelfBillingListQuery) => void;
  reloadSeguradoList: (query: SelfBillingListQuery) => Promise<void>;
  openSeguradoDetail: (row: SelfBillingTableItem) => Promise<void>;
  saveNewSegurado: (params: SelfBillingMutationParams) => Promise<void>;
  saveNewConjuge: (params: SelfBillingMutationParams) => Promise<void>;
  updateSegurado: (params: SelfBillingMutationParams) => Promise<void>;
  removeSelectedSegurados: (params: SelfBillingRemoveParams) => Promise<void>;
}

/**
 * Fetches the sub-stipulator billing preview and returns a map
 * identCertificado -> numVidaPreviaFat. Insureds without a preview (missing from
 * the map or without numVidaPreviaFat) stay in the list with Inactive status.
 * On failure, throws — the list can only be built if this query and
 * getSeguradoList both succeed.
 */
const fetchPreviaFaturamentoByCertificado = async (
  identSubestipulante: number,
): Promise<Map<number, number>> => {
  const consulta = await apiService.maintenanceSegurado.consultarSeguradosFaturamento({
    identSubestipulante,
  });

  if (isSelfBillingApiErrorMessage(consulta?.mensagem) || !consulta?.retorno) {
    throw new Error(
      consulta?.mensagem?.trim()
      || 'Erro ao consultar prévia de faturamento dos segurados',
    );
  }

  const previaByCertificado = new Map<number, number>();
  consulta.retorno.forEach((item) => {
    if (item.numVidaPreviaFat != null) {
      previaByCertificado.set(item.identCertificado, item.numVidaPreviaFat);
    }
  });
  return previaByCertificado;
};

export const useSelfBilling = (): UseSelfBillingReturn => {
  const user = useUserStore((state) => state.user);

  const [isListLoading, setIsListLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [rows, setRows] = useState<SelfBillingTableItem[]>([]);
  const [listCurrentPage, setListCurrentPage] = useState(0);
  const [listTotalPages, setListTotalPages] = useState(0);
  const [listTotalElements, setListTotalElements] = useState(0);

  const [isSaving, setIsSaving] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<NewInsuredFormValues | null>(null);
  const [selectedRow, setSelectedRow] = useState<SelfBillingTableItem | null>(null);
  const [beneficiarios, setBeneficiarios] = useState<SeguradoDetailBeneficiaryItem[]>([]);
  const [conjugueFormValues, setConjugueFormValues] = useState<NewInsuredFormValues | null>(null);

  const resetList = useCallback(() => {
    setRows([]);
    setListError(null);
    setListCurrentPage(0);
    setListTotalPages(0);
    setListTotalElements(0);
  }, []);

  const clearListError = useCallback(() => {
    setListError(null);
  }, []);

  const loadSeguradoList = useCallback(async (
    identSubestipulante: number,
    options?: {
      search?: string;
      page?: number;
      size?: number;
    },
  ): Promise<void> => {
    setIsListLoading(true);
    setListError(null);

    try {
      const searchParams = buildSeguradoListSearchParams(options?.search ?? '');
      const [response, previaByCertificado] = await Promise.all([
        apiService.maintenanceSegurado.getSeguradoList({
          identSubestipulante,
          page: options?.page ?? 0,
          size: options?.size ?? 10,
          ...searchParams,
        }),
        fetchPreviaFaturamentoByCertificado(identSubestipulante),
      ]);

      const mappedRows = response.pagina.content
        .map(mapSeguradoListItemToTableRow)
        .map((row) => {
          const numVidaPreviaFat = previaByCertificado.get(row.identCertificado);
          if (numVidaPreviaFat != null) {
            return {
              ...row,
              numVidaPreviaFat,
              status: SEGURADO_BILLING_STATUS.ATIVO,
            };
          }

          return { ...row, status: SEGURADO_BILLING_STATUS.INATIVO };
        });

      setRows(mappedRows);
      setListCurrentPage(response.pagina.pageNumber);
      setListTotalPages(response.pagina.totalPages);
      setListTotalElements(response.pagina.totalElements);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar lista de segurados';
      setListError(errorMessage);
      setRows([]);
      setListCurrentPage(0);
      setListTotalPages(0);
      setListTotalElements(0);
    } finally {
      setIsListLoading(false);
    }
  }, []);

  const reloadSeguradoList = useCallback(async (query: SelfBillingListQuery): Promise<void> => {
    await loadSeguradoList(query.identSubestipulante, {
      search: query.search,
      page: query.page,
      size: query.size,
    });
  }, [loadSeguradoList]);

  const syncSeguradoList = useCallback((hasSearched: boolean, query: SelfBillingListQuery) => {
    if (!hasSearched) {
      resetList();
      return;
    }

    void loadSeguradoList(query.identSubestipulante, {
      search: query.search,
      page: query.page,
      size: query.size,
    });
  }, [loadSeguradoList, resetList]);

  const resolveSeguradoRowNumVidaPreviaFat = useCallback(async (
    row: SelfBillingTableItem,
  ): Promise<SelfBillingTableItem> => {
    if (row.numVidaPreviaFat != null) {
      return row;
    }

    const consultaResponse = await apiService.maintenanceSegurado.consultarSeguradosFaturamento({
      identSubestipulante: row.identSubestipulante,
      ...buildConsultarSeguradosFaturamentoParams(row),
    });

    if (isSelfBillingApiErrorMessage(consultaResponse.mensagem)) {
      throw new Error(consultaResponse.mensagem ?? 'Não foi possível localizar o segurado');
    }

    const numVidaPreviaFat = findNumVidaPreviaFatFromConsulta(row, consultaResponse.retorno);
    if (numVidaPreviaFat == null) {
      throw new Error('Identificador do segurado não encontrado para alteração');
    }

    return {
      ...row,
      numVidaPreviaFat,
    };
  }, []);

  const removeSegurados = useCallback(async (
    identSubestipulante: number,
    ids: string[],
  ): Promise<void> => {
    if (ids.length === 0) {
      return;
    }

    const rowsToRemove = rows.filter((row) => ids.includes(row.id));
    if (rowsToRemove.length === 0) {
      throw new Error('Nenhum segurado selecionado encontrado na lista atual');
    }

    setIsDeleting(true);
    setListError(null);

    try {
      const resolvedRows = await Promise.all(
        rowsToRemove.map((row) => resolveSeguradoRowNumVidaPreviaFat(row)),
      );

      const request = buildRemoveSeguradoDataRequest({
        idSubestipulante: identSubestipulante,
        codigoUsuarioInclusao: resolveCodigoUsuarioInclusao(user?.idPessoa),
        rows: resolvedRows,
      });

      const response = await apiService.maintenanceSegurado.removeSeguradoData(request);
      if (isSelfBillingApiErrorMessage(response.mensagem)) {
        throw new Error(response.mensagem ?? 'Não foi possível excluir os dados do segurado');
      }
    } catch (err) {
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [rows, resolveSeguradoRowNumVidaPreviaFat, user?.idPessoa]);

  const removeSelectedSegurados = useCallback(async (params: SelfBillingRemoveParams): Promise<void> => {
    await removeSegurados(params.identSubestipulante, params.ids);
    await reloadSeguradoList(params.listQuery);
  }, [removeSegurados, reloadSeguradoList]);

  const resetDetail = useCallback(() => {
    setFormValues(null);
    setSelectedRow(null);
    setBeneficiarios([]);
    setConjugueFormValues(null);
    setDetailError(null);
  }, []);

  const clearDetailError = useCallback(() => {
    setDetailError(null);
  }, []);

  const fetchSeguradoDetail = useCallback(async (
    row: SelfBillingTableItem,
  ): Promise<{
    mergedRow: SelfBillingTableItem;
    formValues: NewInsuredFormValues;
    beneficiarios: SeguradoDetailBeneficiaryItem[];
  }> => {
    if (row.numVidaPreviaFat == null) {
      throw new Error('Identificador do segurado não carregado');
    }

    const response = await apiService.maintenanceSegurado.getSeguradoDetail({
      numVidaPreviaFat: row.numVidaPreviaFat,
    });

    if (isSelfBillingApiErrorMessage(response.mensagem)) {
      throw new Error(response.mensagem ?? 'Não foi possível carregar os dados do segurado');
    }

    const detailRetorno = response.retorno?.[0];
    const detailPayload = detailRetorno?.jsonSegurado?.[0];
    if (!detailPayload) {
      throw new Error('Dados do segurado não encontrados');
    }

    return {
      mergedRow: mapSeguradoDetailToTableRow(detailPayload, row),
      formValues: mapSeguradoDetailToFormValues(detailPayload),
      beneficiarios: detailRetorno?.jsonBeneficiarios ?? [],
    };
  }, []);

  const loadSeguradoDetail = useCallback(async (row: SelfBillingTableItem) => {
    setSelectedRow(row);
    setDetailError(null);
    setIsLoadingDetail(true);

    try {
      const { mergedRow, formValues: detailFormValues, beneficiarios: detailBeneficiarios } = await fetchSeguradoDetail(row);
      setSelectedRow(mergedRow);
      setFormValues(detailFormValues);
      setBeneficiarios(detailBeneficiarios);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar dados do segurado';
      setDetailError(errorMessage);
      setFormValues(mapSeguradoTableRowToFormValues(row));
      setBeneficiarios([]);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [fetchSeguradoDetail]);

  const openSeguradoDetail = useCallback(async (row: SelfBillingTableItem) => {
    setSelectedRow(row);
    setDetailError(null);
    setIsLoadingDetail(true);
    setConjugueFormValues(null);

    try {
      const resolvedRow = await resolveSeguradoRowNumVidaPreviaFat(row);
      setSelectedRow(resolvedRow);

      const {
        mergedRow,
        formValues: detailFormValues,
        beneficiarios: detailBeneficiarios,
      } = await fetchSeguradoDetail(resolvedRow);
      setSelectedRow(mergedRow);
      setFormValues(detailFormValues);
      setBeneficiarios(detailBeneficiarios);

      const conjugueFromList = rows.find((item) => (
        item.matricula?.trim() === resolvedRow.matricula?.trim()
        && item.tipoSeguradoCodigo?.toUpperCase() === 'C'
      ));

      if (conjugueFromList) {
        const resolvedConjugue = await resolveSeguradoRowNumVidaPreviaFat(conjugueFromList);
        const conjugueDetail = await fetchSeguradoDetail(resolvedConjugue);
        setConjugueFormValues(conjugueDetail.formValues);
      } else {
        const consultaResponse = await apiService.maintenanceSegurado.consultarSeguradosFaturamento({
          identSubestipulante: resolvedRow.identSubestipulante,
          ...buildConsultarSeguradosFaturamentoParams(resolvedRow),
        });

        const conjugueItem = consultaResponse.retorno?.find(
          (item) => item.tipoSegurado?.trim().toUpperCase() === 'C',
        );

        if (conjugueItem?.numVidaPreviaFat != null) {
          const conjugueDetailResponse = await apiService.maintenanceSegurado.getSeguradoDetail({
            numVidaPreviaFat: conjugueItem.numVidaPreviaFat,
          });
          const conjuguePayload = conjugueDetailResponse.retorno?.[0]?.jsonSegurado?.[0];
          setConjugueFormValues(
            conjuguePayload ? mapSeguradoDetailToFormValues(conjuguePayload) : null,
          );
        } else {
          setConjugueFormValues(null);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao buscar dados do segurado';
      setDetailError(errorMessage);
      setFormValues(mapSeguradoTableRowToFormValues(row));
      setBeneficiarios([]);
      setConjugueFormValues(null);
    } finally {
      setIsLoadingDetail(false);
    }
  }, [fetchSeguradoDetail, resolveSeguradoRowNumVidaPreviaFat, rows]);

  const addSeguradoData = useCallback(async (
    idSubestipulante: number,
    contrato: number,
    values: NewInsuredFormValues,
  ): Promise<void> => {
    setIsAdding(true);
    setDetailError(null);

    try {
      const request = buildAddSeguradoDataRequest({
        idSubestipulante,
        contrato,
        codigoUsuarioInclusao: resolveCodigoUsuarioInclusao(user?.idPessoa),
        formValues: values,
      });

      const response = await apiService.maintenanceSegurado.addSeguradoData(request);
      assertAddSeguradoDataResponse(response);
    } catch (err) {
      const inconsistencies = parseSeguradoInconsistenciesFromError(err);
      if (inconsistencies.length > 0) {
        throw new SeguradoInconsistencyError(inconsistencies, err);
      }
      throw err;
    } finally {
      setIsAdding(false);
    }
  }, [user?.idPessoa]);

  const saveNewSegurado = useCallback(async (params: SelfBillingMutationParams): Promise<void> => {
    setIsAdding(true);
    setDetailError(null);

    try {
      const codigoUsuarioInclusao = resolveCodigoUsuarioInclusao(user?.idPessoa);
      const request = buildAddSeguradoDataRequest({
        idSubestipulante: params.identSubestipulante,
        contrato: params.contrato,
        codigoUsuarioInclusao,
        formValues: params.formValues,
        conjugueFormValues: params.conjugueFormValues,
        beneficiarios: params.beneficiarios,
      });

      const response = await apiService.maintenanceSegurado.addSeguradoData(request);
      assertAddSeguradoDataResponse(response);

      await reloadSeguradoList(params.listQuery);
    } catch (err) {
      const inconsistencies = parseSeguradoInconsistenciesFromError(err);
      if (inconsistencies.length > 0) {
        throw new SeguradoInconsistencyError(inconsistencies, err);
      }

      const errorMessage = err instanceof Error ? err.message : 'Erro ao incluir dados do segurado';
      setDetailError(errorMessage);
      throw err;
    } finally {
      setIsAdding(false);
    }
  }, [reloadSeguradoList, user?.idPessoa]);

  const addConjugeSeguradoData = useCallback(async (
    idSubestipulante: number,
    contrato: number,
    values: NewInsuredFormValues,
  ): Promise<void> => {
    setIsAdding(true);
    setDetailError(null);

    try {
      const request = buildAddConjugeSeguradoDataRequest({
        idSubestipulante,
        contrato,
        codigoUsuarioInclusao: resolveCodigoUsuarioInclusao(user?.idPessoa),
        formValues: values,
      });

      const response = await apiService.maintenanceSegurado.addConjugeSeguradoData(request);
      assertAddConjugeSeguradoDataResponse(response);
    } catch (err) {
      const inconsistencies = parseSeguradoInconsistenciesFromError(err);
      if (inconsistencies.length > 0) {
        throw new SeguradoInconsistencyError(inconsistencies, err);
      }
      throw err;
    } finally {
      setIsAdding(false);
    }
  }, [user?.idPessoa]);

  const saveNewConjuge = useCallback(async (params: SelfBillingMutationParams): Promise<void> => {
    await addConjugeSeguradoData(
      params.identSubestipulante,
      params.contrato,
      params.formValues,
    );
    await reloadSeguradoList(params.listQuery);
  }, [addConjugeSeguradoData, reloadSeguradoList]);

  const updateSeguradoData = useCallback(async (
    idSubestipulante: number,
    contrato: number,
    values: NewInsuredFormValues,
  ): Promise<void> => {
    if (!selectedRow) {
      throw new Error('Dados do segurado não carregados');
    }

    setIsSaving(true);
    setDetailError(null);

    try {
      const resolvedRow = await resolveSeguradoRowNumVidaPreviaFat(selectedRow);
      if (resolvedRow.numVidaPreviaFat !== selectedRow.numVidaPreviaFat) {
        setSelectedRow(resolvedRow);
      }

      const request = buildUpdateSeguradoDataRequest({
        idSubestipulante,
        contrato,
        codigoUsuarioInclusao: resolveCodigoUsuarioInclusao(user?.idPessoa),
        formValues: values,
        row: resolvedRow,
        beneficiarios,
      });

      const response = await apiService.maintenanceSegurado.updateSeguradoData(request);
      assertUpdateSeguradoDataResponse(response);
    } catch (err) {
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [beneficiarios, resolveSeguradoRowNumVidaPreviaFat, selectedRow, user?.idPessoa]);

  const updateSegurado = useCallback(async (params: SelfBillingMutationParams): Promise<void> => {
    await updateSeguradoData(
      params.identSubestipulante,
      params.contrato,
      params.formValues,
    );
    await reloadSeguradoList(params.listQuery);
  }, [reloadSeguradoList, updateSeguradoData]);

  return {
    list: {
      isLoading: isListLoading,
      isDeleting,
      error: listError,
      rows,
      currentPage: listCurrentPage,
      totalPages: listTotalPages,
      totalElements: listTotalElements,
      clearError: clearListError,
      reset: resetList,
      loadSeguradoList,
      removeSegurados,
    },
    detail: {
      isSaving,
      isAdding,
      isLoadingDetail,
      error: detailError,
      formValues,
      selectedRow,
      beneficiarios,
      hasBeneficiarios: hasSeguradoBeneficiaries(beneficiarios),
      conjugueFormValues,
      hasConjugue: conjugueFormValues != null,
      setBeneficiarios,
      setConjugueFormValues,
      clearError: clearDetailError,
      reset: resetDetail,
      loadSeguradoDetail,
      addSeguradoData,
      addConjugeSeguradoData,
      updateSeguradoData,
    },
    syncSeguradoList,
    reloadSeguradoList,
    openSeguradoDetail,
    saveNewSegurado,
    saveNewConjuge,
    updateSegurado,
    removeSelectedSegurados,
  };
};
